import type { SweetSpotEvaluationResult, SweetSpotPointResult } from './types';
import type { EvaluationRun } from './sweetSpotEvaluationEngine';
import { buildSweetSpotEvaluationResult } from './sweetSpotEvaluationResult';

export function adaptEvaluationRunToLegacyResult(run: EvaluationRun): SweetSpotEvaluationResult | null {
  const result = buildSweetSpotEvaluationResult(run.points, {
    wellId: run.wellId,
    depthUnit: 'm',
    sourceCurveIds: run.inputSnapshot.sourceCurveIds,
    parameterConfig: run.config.parameters,
    highQualityThreshold: run.config.gradeThresholds.classII,
    poorQualityThreshold: run.config.gradeThresholds.classIII,
    ssiFormula: run.combinationFunction === 'geometric_mean'
      ? 'sqrt(RQ * CQ)（任一组缺失时不计算完整SSI）'
      : run.methodId === 'legacy-rule'
        ? '0.5 * RQ + 0.5 * CQ（单组有效时使用该组评分）'
        : '0.5 * RQ + 0.5 * CQ（任一组缺失时不计算完整SSI）',
  });
  if (!result) return null;
  return {
    ...result,
    evaluationConfig: {
      ...result.evaluationConfig,
      runId: run.runId,
      methodId: run.methodId,
      methodVersion: run.methodVersion,
      definitionHash: run.definitionHash,
      scopeLabel: run.scopeLabel,
      combinationFunction: run.combinationFunction,
      sourceEvaluationConfig: run.config,
    },
    generatedAt: run.createdAt,
  };
}

export function buildBackendEvaluations(points: SweetSpotPointResult[]): unknown[] {
  const depthSorted = points
    .filter((point) => Number.isFinite(point.depth))
    .slice()
    .sort((a, b) => a.depth - b.depth);
  const valid = depthSorted.filter((point) => (
    typeof point.ssi === 'number'
    && Number.isFinite(point.ssi)
    && point.grade != null
  ));
  if (!valid.length) return [];
  const positiveSteps = depthSorted.slice(1)
    .map((point, index) => point.depth - depthSorted[index].depth)
    .filter((step) => Number.isFinite(step) && step > 0)
    .sort((a, b) => a - b);
  const sampleStep = positiveSteps.length
    ? positiveSteps[Math.floor(positiveSteps.length / 2)]
    : 1;
  const top = valid[0].depth;
  // Each evaluated sample represents one grid cell. Stop after the final
  // evaluated cell instead of extending a 40 m segment across invalid data.
  const bottom = valid[valid.length - 1].depth + sampleStep;
  const segmentLength = 40;
  const average = (rows: SweetSpotPointResult[], field: 'rq' | 'cq' | 'ssi') => {
    const values = rows.map((row) => row[field]).filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
    return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
  };
  const output: unknown[] = [];
  for (let segmentTop = top; segmentTop < bottom; segmentTop += segmentLength) {
    const segmentBottom = Math.min(segmentTop + segmentLength, bottom);
    const rows = valid.filter((point) => point.depth >= segmentTop && point.depth < segmentBottom);
    const ssi = average(rows, 'ssi');
    if (ssi == null) continue;
    const grade = rows.find((row) => row.grade)?.grade;
    if (!grade) continue;
    const rq = average(rows, 'rq');
    const cq = average(rows, 'cq');
    output.push({
      top_depth: Number(segmentTop.toFixed(2)),
      bottom_depth: Number(segmentBottom.toFixed(2)),
      grade,
      composite_score: Number(ssi.toFixed(4)),
      recommended_for_frac: grade === 'I' || grade === 'II',
      notes: `EvaluationRun适配: RQ${rq == null ? '无' : rq.toFixed(2)}, CQ${cq == null ? '无' : cq.toFixed(2)}, SSI${ssi.toFixed(2)}`,
    });
  }
  return output;
}
